/**
 * FooterWrapper Component
 *
 * A small layout utility component that standardizes the wrapper structure for
 * table footer content. It applies consistent styling, dynamic classes, and
 * optional height or ref forwarding for measurement or DOM interaction.
 *
 * This component is used by other table footer components (e.g., ViewPerCurrency)
 * to avoid duplication of styling logic and ensure uniformity across all footer
 * types.
 *
 * Props:
 *  - `column` (string): The column identifier. Passed to `getTableClass` to
 *    dynamically assign column-specific table classes.
 *
 *  - `className` (string, optional): Additional CSS classes to merge into the
 *    wrapper container. Defaults to an empty string.
 *
 *  - `isCCYColumn` (boolean, optional): Highlights the wrapper as belonging to the
 *    CCY (currency) column. Influences how `getTableClass` resolves classes.
 *
 *  - `isLastColumnBeforeCurrency` (boolean, optional): Marks the wrapper as belonging to the
 *    last column before CCY. Applies a `"view_per_currency"` CSS class when true.
 *
 *  - `height` (number | string, optional): Allows the wrapper to take a fixed
 *    height. Typically used for aligning geo/location columns or dynamically
 *    controlled footer heights.
 *
 *  - `children` (ReactNode): The rendered contents inside the wrapper. This is
 *    typically footer rows, buttons, or preview components.
 *
 *  - `refProp` (Ref<HTMLDivElement>, optional): A forwarded ref for measuring
 *    or manipulating the wrapper externally (e.g., calculating dynamic dialog
 *    height in `ViewPerCurrency`).
 *
 * Behavior:
 *  - The component always renders a `div` with baseline `table_footer` styling.
 *  - Additional classes are added based on column type, last column before CCY/CCY flags,
 *    and user-provided `className`.
 *  - If `height` is provided, it applies inline height styling.
 *
 * Usage Example:
 *  <FooterWrapper
 *    column="CCY"
 *    isCCYColumn
 *    className="highlight"
 *    refProp={someRef}
 *    isLastColumnBeforeCurrency
 *    height={120}
 *  >
 *    <SomeFooterContent />
 *  </FooterWrapper>
 */

import { getTableClass } from '../../../../../util/table-helper';
import { usePageVisibleColumns } from '../../../../../hook/usePageVisibleColumns';

interface FooterWrapperProps {
  column: string;
  className?: string;
  isCCYColumn?: boolean;
  isLastColumnBeforeCurrency?: boolean;
  height?: number | string;
  children: React.ReactNode;
  refProp?: React.Ref<HTMLDivElement>;
}

const FooterWrapper: React.FC<FooterWrapperProps> = ({
  column,
  className = '',
  isCCYColumn = false,
  isLastColumnBeforeCurrency = false,
  height,
  children,
  refProp,
}) => {
  const { visibleColumns } = usePageVisibleColumns();

  return (
    <div
      ref={refProp}
      className={`table_footer ${getTableClass(column, 'footer', visibleColumns, isCCYColumn)} ${
        isLastColumnBeforeCurrency ? 'view_per_currency' : ''
      } ${className}`}
      style={height ? { height } : undefined}
    >
      {children}
    </div>
  );
};

export default FooterWrapper;
